import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  visitTypes: [],
  visitTypeCategories: [], // List of visit type categories from /visit-types/init
  selectedVisitType: null,
  loading: false,
  error: null,
  successMessage: null,
  activeTab: 0, // Tab state: 0 = VisitTypes, 1 = VisitTypeCategories
  // Pagination
  page: 1,
  pageSize: 10,
  total: 0,
  // Sorting
  sortColumn: 'name',
  sortDirection: 'asc',
  // Filtering
  filterName: '',
};

const visitTypesSlice = createSlice({
  name: 'visitTypes',
  initialState,
  reducers: {
    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.visitTypeCategories = action.payload.visitTypeCategories || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch visit types
    fetchVisitTypes: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchVisitTypesSuccess: (state, action) => {
      state.loading = false;
      state.visitTypes = action.payload.data;
      state.total = action.payload.meta.total;
      state.error = null;
    },
    fetchVisitTypesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch visit type by ID
    fetchVisitTypeById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchVisitTypeByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedVisitType = {
        ...action.payload,
        categoryId: action.payload.categoryId || null,
      };
      state.error = null;
    },
    fetchVisitTypeByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Add visit type
    addVisitType: (state) => {
      state.loading = true;
      state.error = null;
    },
    addVisitTypeSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    addVisitTypeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update visit type
    updateVisitType: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateVisitTypeSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    updateVisitTypeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete visit type
    deleteVisitType: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteVisitTypeSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    deleteVisitTypeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete
    bulkDeleteVisitTypes: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteVisitTypesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    bulkDeleteVisitTypesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filtering
    },

    // Tab management
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchVisitTypes,
  fetchVisitTypesSuccess,
  fetchVisitTypesFailure,
  fetchVisitTypeById,
  fetchVisitTypeByIdSuccess,
  fetchVisitTypeByIdFailure,
  addVisitType,
  addVisitTypeSuccess,
  addVisitTypeFailure,
  updateVisitType,
  updateVisitTypeSuccess,
  updateVisitTypeFailure,
  deleteVisitType,
  deleteVisitTypeSuccess,
  deleteVisitTypeFailure,
  bulkDeleteVisitTypes,
  bulkDeleteVisitTypesSuccess,
  bulkDeleteVisitTypesFailure,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  setActiveTab,
  clearError,
  clearSuccessMessage,
} = visitTypesSlice.actions;

// Selectors
export const selectVisitTypes = (state) => state.visitTypes?.visitTypes || [];
export const selectSelectedVisitType = (state) => state.visitTypes?.selectedVisitType;
export const selectVisitTypesLoading = (state) => state.visitTypes?.loading || false;
export const selectVisitTypesError = (state) => state.visitTypes?.error;
export const selectVisitTypesSuccessMessage = (state) => state.visitTypes?.successMessage;
export const selectVisitTypesTotal = (state) => state.visitTypes?.total || 0;
export const selectVisitTypesPage = (state) => state.visitTypes?.page || 1;
export const selectVisitTypesPageSize = (state) => state.visitTypes?.pageSize || 10;
export const selectVisitTypesSortColumn = (state) => state.visitTypes?.sortColumn || 'name';
export const selectVisitTypesSortDirection = (state) => state.visitTypes?.sortDirection || 'asc';
export const selectVisitTypesFilterName = (state) => state.visitTypes?.filterName || '';
export const selectVisitTypeCategories = (state) => state.visitTypes?.visitTypeCategories || [];
export const selectActiveTab = (state) => state.visitTypes?.activeTab || 0;

export default visitTypesSlice.reducer;
